// Ecosystem Edge Capability 04 — Bettor Archetype → Surface routing
// (decision-as-code, pure, no I/O — mirrors archetypeMapping.ts).
//
// The doc describes FIVE archetype experiences. STRIKZA's canonical
// store has FOUR archetypes (user_betting_profile.archetype — single
// source of truth, same ruling as archetypeMapping.ts). The doc's
// fifth, "Analyst" (full Debate output, all agent reasoning,
// Resilience Audit), is NOT a fifth stored archetype — per the same
// precedent ("jackpot_hunter has no doc survey equivalent") it is an
// INTENSITY layered on top of any base archetype, derived from the
// EXISTING profile_confidence tier ('expert' ⇒ analyst surfacing).
// No new enum, no fabricated archetype.
//
// Doc experience → canonical archetype (documented so it's adjustable):
//   Accumulator Builder → jackpot_hunter
//       POWERBALL prominent, Odds Composer primary, multi-leg default.
//   Value Hunter        → value_seeker
//       edge% on every card, CLV the primary metric, mispricing badges.
//   Goal Setter         → disciplined_compounder
//       Sika target progress bar dominant, ROLLOVER framed by milestone.
//   Casual User         → intelligence_learner
//       one daily recommendation, minimal complexity, one action.
//   Analyst             → ANY archetype where profile_confidence==='expert'
//       full Debate Moderator output + Resilience Audit default.
//
// This module ONLY decides the surface config. Consuming it to
// re-order the live dashboard is a separate, design-gated step — this
// slice is the routing primitive (zero behavioural change until read),
// exactly how archetypeMapping.ts shipped before its survey consumed it.

import type { Archetype, ProfileConfidence } from "../slips/useBettingProfile.ts";

/** What the surface emphasises for a given user. Consumers read this
 *  to choose the primary module / dashboard ordering / card density. */
export interface ArchetypeSurface {
  /** The module that should lead the authed surface for this user. */
  primaryModule:
    | "powerball"
    | "signals"
    | "sika"
    | "daily_pick"
    | "debate";
  /** Ordered emphasis hint for dashboard panels (first = most prominent). */
  emphasis: string[];
  /** Default tool the archetype lands in. */
  defaultTool: "odds_composer" | "signals" | "sika" | "daily" | "debate";
  /** Surface density. casual = one action; dense = full reasoning. */
  complexity: "minimal" | "standard" | "dense";
  /** Show edge% on every signal card (Value Hunter). */
  showEdgeOnCards: boolean;
  /** CLV is the primary dashboard metric (Value Hunter). */
  clvPrimaryMetric: boolean;
  /** Sika target progress bar is the dominant UI element (Goal Setter). */
  sikaBarDominant: boolean;
  /** Full Debate Moderator output + Resilience Audit default (Analyst). */
  analystMode: boolean;
}

const BASE: Record<Archetype, ArchetypeSurface> = {
  jackpot_hunter: {
    primaryModule: "powerball",
    emphasis: ["powerball", "ladder", "signals", "sika"],
    defaultTool: "odds_composer",
    complexity: "standard",
    showEdgeOnCards: false,
    clvPrimaryMetric: false,
    sikaBarDominant: false,
    analystMode: false,
  },
  value_seeker: {
    primaryModule: "signals",
    emphasis: ["signals", "fair_price", "clv", "ladder"],
    defaultTool: "signals",
    complexity: "dense",
    showEdgeOnCards: true,
    clvPrimaryMetric: true,
    sikaBarDominant: false,
    analystMode: false,
  },
  disciplined_compounder: {
    primaryModule: "sika",
    emphasis: ["sika", "rollover", "signals", "powerball"],
    defaultTool: "sika",
    complexity: "standard",
    showEdgeOnCards: false,
    clvPrimaryMetric: false,
    sikaBarDominant: true,
    analystMode: false,
  },
  intelligence_learner: {
    primaryModule: "daily_pick",
    emphasis: ["daily_pick", "academy", "signals"],
    defaultTool: "daily",
    complexity: "minimal",
    showEdgeOnCards: false,
    clvPrimaryMetric: false,
    sikaBarDominant: false,
    analystMode: false,
  },
};

/** A sensible neutral default when the archetype is not yet known
 *  (profile not built / new user) — never guess an archetype. */
export const DEFAULT_SURFACE: ArchetypeSurface = {
  primaryModule: "daily_pick",
  emphasis: ["daily_pick", "signals", "sika"],
  defaultTool: "daily",
  complexity: "standard",
  showEdgeOnCards: false,
  clvPrimaryMetric: false,
  sikaBarDominant: false,
  analystMode: false,
};

/**
 * Resolve the surface for a user. `archetype` null ⇒ DEFAULT_SURFACE
 * (honest: no archetype detected yet, no fabrication). When
 * `confidence === 'expert'` the Analyst intensity is layered on TOP
 * of the base archetype (dense + debate default + analystMode) — the
 * doc's fifth experience, derived from existing data, not a 5th type.
 */
export function surfaceForArchetype(
  archetype: Archetype | null,
  confidence?: ProfileConfidence | null,
): ArchetypeSurface {
  const base = archetype ? { ...BASE[archetype] } : { ...DEFAULT_SURFACE };
  if (confidence === "expert") {
    return {
      ...base,
      complexity: "dense",
      defaultTool: "debate",
      analystMode: true,
      emphasis: ["debate", ...base.emphasis.filter((e) => e !== "debate")],
    };
  }
  return base;
}
